import type { LocationQuery } from 'vue-router'
import type { useMainStore } from '@/store'
import { buildQueryString } from '@/utils/url-action'

// issue #755: 初回訪問時の流入元パラメータ(広告のクリックIDを含む)を扱う。
//
// 1. 流入時: クエリを検知してCookieに保存する(Cookieの発行自体はRails側)
// 2. 会員登録時: RailsがCookieの内容をDBへ保存する
// 3. 登録後: DBの値をuser_info API(store.my_profile)経由でdataLayerへ載せる
//
// dataLayerへ載せる値は「DBに保存済みの確定値」なので、会員登録前は空になる。
// Cookieはあくまで登録時までの受け渡し用で、dataLayerのソースには使わない。
//
// Cookieはこのファイルからは書かず、Railsの支援エンドポイント経由でサーバ発行する。
// Safari(ITP)では`document.cookie`で書いたCookieが最長7日に切り詰められ、
// 「初回訪問からユーザー登録まで90日」の要件を満たせないため。
// (既存のafb/yahoo流入計測と同じ作法。`utils/tracking/afb.js` 参照)
//
// 一度保存したら上書きしない(旧情報を優先する)。ホワイトリスト対象のパラメータが
// 1件もない訪問ではCookieを発行しないため、自然流入で初回訪問した後に広告経由で
// 再訪した場合は広告側が記録される。

export const FIRST_VISIT_PARAMS_COOKIE_KEY = 'ssr_first_visit_params'

// ホワイトリスト。Rails側の UserFirstVisitParam::PARAM_KEYS と一致させること。
export const FIRST_VISIT_PARAM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'fbclid', 'ttclid', 'gclid'] as const

// Cookieへ保存した日時(サーバ時刻)が入るキー
const SAVED_AT_KEY = 'saved_at'

// dataLayerのキーに付けるprefix
const GTM_KEY_PREFIX = 'first_visit_'

// Rails側のカラム長に合わせる。Cookieサイズの肥大も防ぐ
const MAX_VALUE_LENGTH = 255

const API_PATH = 'supports/generate_cookie_first_visit'

type MainStore = ReturnType<typeof useMainStore>
type FirstVisitParamValues = Record<string, string>
// router.afterEach の RouteLocationNormalized と currentRoute の RouteLocationNormalizedLoaded の
// 双方を受け取れるよう、必要なプロパティだけを要求する
type RouteWithQuery = { query: LocationQuery }

// Cookieに保存済みの流入元パラメータ。SSR時のCookieヘッダ由来なのでHttpOnlyでも参照できる。
// 無駄なリクエストを避ける判定にのみ使う(dataLayerのソースはDB由来のmy_profile)。
export function savedFirstVisitParams(store: MainStore): FirstVisitParamValues | undefined {
  const saved = store.ssrCookie[FIRST_VISIT_PARAMS_COOKIE_KEY]
  if (!saved || typeof saved !== 'object') return undefined
  return saved as FirstVisitParamValues
}

// 流入時: ホワイトリスト対象のクエリを検知して、Cookie発行をRailsへ依頼する。
// Cookieはこのレスポンスの Set-Cookie でブラウザにセットされ、次のSSRで
// store.ssrCookie にも載るため、フロントから状態を持つ必要はない。
export function captureFirstVisitParams(route: RouteWithQuery, store: MainStore): void {
  if (savedFirstVisitParams(store)) return // 既にCookieがあれば発行を依頼しない(旧情報を優先する)
  const params = extractParams(route.query)
  if (Object.keys(params).length === 0) return

  // 分析用の副作用なので、失敗しても画面には影響させない(未処理のrejectionを防ぐ)。
  // 失敗しても次のページ遷移・次回訪問で再試行される。
  // 上書き防止はRails側でもCookieの有無を見ているため、重複して呼ばれても旧情報は保たれる。
  store.dispatch('apiGet', `${API_PATH}${buildQueryString(params)}`).catch(() => undefined)
}

// GTM.defaultDataに載せるデータを返す。
// ソースはDBに保存済みの値(user_info API → store.my_profile)なので、会員登録前は空になる。
//
// 値が無い場合もキーを空文字で出す(GTM.defaultDataの customer_id / profile_* と同じ作法)。
// dataLayerは追記型でGTMも前回の値を保持するため、キーを省くとログアウトや
// ユーザー切り替えの後も前のユーザーの値がGTM上に残ってしまう。
export function firstVisitParamsGtmData(store: MainStore): Record<string, string> {
  const saved = store.$state.my_profile?.first_visit_params

  return [...FIRST_VISIT_PARAM_KEYS, SAVED_AT_KEY].reduce((data: Record<string, string>, key) => {
    const value = saved?.[key]
    data[`${GTM_KEY_PREFIX}${key}`] = typeof value === 'string' ? value : ''
    return data
  }, {})
}

// --- 補助ヘルパー関数 ---

function extractParams(query: LocationQuery): FirstVisitParamValues {
  return FIRST_VISIT_PARAM_KEYS.reduce((params: FirstVisitParamValues, key) => {
    const raw = query[key]
    const value = Array.isArray(raw) ? raw[0] : raw // 同名パラメータが複数渡された場合は先頭のみ採用する
    if (typeof value !== 'string') return params

    const trimmed = value.trim().slice(0, MAX_VALUE_LENGTH)
    if (trimmed !== '') params[key] = trimmed
    return params
  }, {})
}
